import { Link } from "react-router-dom";
import { ArrowRight, Users, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import serviceStaffAug from "@/assets/service-staff-aug.jpg";
import serviceManaged from "@/assets/service-managed.jpg";

const services = [
  {
    icon: Users,
    name: "Staff Augmentation",
    testid: "service-staff-augmentation",
    image: serviceStaffAug,
    imageAlt: "Engineers collaborating on a whiteboard",
    desc:
      "Scale your engineering teams with vetted talent on the model that fits — remote roles, permanent hires, or fixed-term contract engagements. We source senior engineers, architects, and specialists across product engineering, DevOps, QA, SAP, Enterprise Applications, AI/ML, and data analytics — and ramp them up in days, not months.",
    bullets: ["Remote jobs", "Permanent hires", "Contract hires"],
  },
  {
    icon: Layers,
    name: "Managed Services",
    testid: "service-managed-services",
    image: serviceManaged,
    imageAlt: "A managed delivery team in a working session",
    desc:
      "When you need outcomes, not just resources, we run the engagement end-to-end. From discovery and design through delivery and ongoing operations, our managed teams own the roadmap so you can stay focused on the business — covering application development, digital transformation, AI/ML, and data programs.",
    bullets: ["End-to-end delivery", "Dedicated pods", "Operational ownership"],
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-16">
        <div className="text-xs uppercase tracking-[0.18em] text-orange-600 font-semibold mb-4 mm-fade-up">
          Services
        </div>
        <h1 className="mm-fade-up mm-delay-1 font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-900 max-w-3xl leading-[1.05]">
          Two ways to work with us.
        </h1>
        <p className="mm-fade-up mm-delay-2 mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
          Plug elite specialists into your existing teams, or hand us the problem and we'll deliver. Choose the engagement model that matches your stage and momentum.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20 lg:pb-28">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {services.map((s) => (
            <article
              key={s.name}
              data-testid={s.testid}
              className="group border border-slate-200 rounded-xl overflow-hidden bg-white hover:border-slate-900 transition-colors flex flex-col"
            >
              <div className="relative w-full h-52 overflow-hidden bg-slate-100">
                <img
                  src={s.image}
                  alt={s.imageAlt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-8 lg:p-10 flex flex-col flex-1">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-md bg-blue-600 text-white mb-6">
                  <s.icon size={22} />
                </span>
                <h2 className="font-heading text-2xl lg:text-3xl font-medium text-slate-900 tracking-tight">
                  {s.name}
                </h2>
                <p className="mt-4 text-slate-600 leading-relaxed text-sm sm:text-base">
                  {s.desc}
                </p>
                <ul className="mt-6 flex flex-wrap gap-2">
                  {s.bullets.map((b) => (
                    <li
                      key={b}
                      className="text-xs uppercase tracking-wider text-slate-700 border border-slate-200 rounded-full px-3 py-1.5 bg-slate-50"
                    >
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <Button
                    asChild
                    variant="outline"
                    className="border-slate-300 text-slate-900 hover:bg-slate-50 rounded-md h-11"
                  >
                    <Link to="/looking-for">
                      Discuss your needs <ArrowRight className="ml-2" size={16} />
                    </Link>
                  </Button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
